import {persistReducer, PURGE} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {AnyAction} from 'redux';

import common from '../../../../config/common.json';
import {pathnames} from '../../_common/constants/pathnames';
import {PagePermission, User} from '../types';

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  RegisterToken: '[Register Token] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  CatchError: '[Catch Error] Action',
  UpdatePermissions: '[Update Permissions] Action'
};

type AuthState = {
  user: User | null,
  accessToken: string,
  refreshToken: string,
  idToken: string,
  greenInvoiceApiKey: string,
  greenInvoiceSecret: string,
  greenInvoicePaymentPluginID: string,
  expire: number | null,
  defaultPage: string,
  pages: PagePermission[] | null,
  adminPermissionsLevel: number | null,
  receivedAt: Date | null,
  error: string | null,
};

const initialAuthState: AuthState = {
  user: null,
  accessToken: '',
  refreshToken: '',
  idToken: '',
  greenInvoiceApiKey: '',
  greenInvoiceSecret: '',
  greenInvoicePaymentPluginID: '',
  expire: null,
  defaultPage: '',
  pages: null, // {pageName: string, permissionLevel: 'full' | 'view' | 'block'}[] | null
  adminPermissionsLevel: null,
  receivedAt: null,
  error: null,
};

const defaultUser = {
  firstName: 'Admin',
  lastName: 'user'
};

export const reducer = persistReducer(
  {
    storage,
    key: 'accuraiser-admin-auth',
    whitelist: [
      'user',
      'accessToken',
      'greenInvoiceApiKey',
      'greenInvoiceSecret',
      'greenInvoicePaymentPluginID',
      'expire',
      'refreshToken',
      'defaultPage',
      'pages',
      'adminPermissionsLevel'
    ]
  },
  (state: AuthState = initialAuthState, action: AnyAction) => {
    switch (action.type) {
      case actionTypes.Login: {
        const {
          tokens,
          user,
          greenInvoiceApiKey,
          greenInvoiceSecret,
          greenInvoicePaymentPluginID,
          pages,
          adminPermissionsLevel,
          receivedAt,
        } = action.payload;
        user.email = user.attributes.email;
        if (!user.attributes.name) {
          user.firstName = defaultUser.firstName;
          user.lastName = defaultUser.lastName;
        } else {
          user.firstName = user.attributes.name;
          user.lastName = user.attributes.family_name || '';
        }
        if (!user.lastName) {
          user.initials = `${user.firstName[0].toUpperCase()}`;
        } else {
          user.initials = `${user.firstName[0].toUpperCase()}${user.lastName[0].toUpperCase()}`;
        }
        user.fullName = `${user.firstName} ${user.lastName}`;

        return {
          ...tokens,
          user: user || defaultUser,
          greenInvoiceApiKey,
          greenInvoiceSecret,
          greenInvoicePaymentPluginID,
          pages,
          adminPermissionsLevel,
          defaultPage: findDefaultPage(pages),
          receivedAt
        };
      }

      case actionTypes.RegisterToken: {
        const {accessToken, expire, receivedAt} = action.payload;
        if (!state.receivedAt || receivedAt > state.receivedAt) {
          return {...state, accessToken, expire, receivedAt};
        } else {
          return {...state};
        }
      }

      case PURGE:
      case actionTypes.Logout: {
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const {user} = action.payload;
        return {...state, user};
      }

      case actionTypes.SetUser: {
        const {user} = action.payload;
        return {...state, user};
      }

      case actionTypes.CatchError: {
        const error = `AUTH: ${action.payload.error}`;
        return {...state, error};
      }

      case actionTypes.UpdatePermissions: {
        const {permissions} = action.payload;
        return {
          ...state,
          pages: permissions,
          defaultPage: findDefaultPage(permissions)
        };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (
    tokens: {
      accessToken: string,
      refreshToken: string,
      idToken: string,
      expire: number
    },
    user: User,
    greenInvoiceApiKey: string,
    greenInvoiceSecret: string,
    greenInvoicePaymentPluginID: string,
    pages: PagePermission[],
    adminPermissionsLevel: number,
    receivedAt: Date,
  ) => ({
    type: actionTypes.Login,
    payload: {
      tokens,
      user,
      greenInvoiceApiKey,
      greenInvoiceSecret,
      pages,
      adminPermissionsLevel,
      greenInvoicePaymentPluginID,
      receivedAt,
    }
  }),
  registerToken: ({accessToken, expire, receivedAt}: {
    accessToken: string, expire: number, receivedAt: Date
  }) => ({
    type: actionTypes.RegisterToken,
    payload: {accessToken, expire, receivedAt},
  }),
  logout: () => ({type: actionTypes.Logout}),
  catchError: (error: string) => ({type: actionTypes.CatchError, payload: {error}}),
  updatePermissions: (permissions: PagePermission[]) => ({type: actionTypes.UpdatePermissions, payload: {permissions}}),
};

const findDefaultPage = (pages: PagePermission[]) => {
  const [pageMarkedAsDefault] = pages.filter(page => page.default);
  if (Boolean(pageMarkedAsDefault)) {
    const [defaultPage] = Object.values(pathnames).filter(page => page === pageMarkedAsDefault.pageName);
    return defaultPage || common.defaultPage;
  } else {
    return common.defaultPage;
  }
};
